import Vue from "vue";
import { mapGetters, mapMutations, mapState } from "vuex";

import actionBarComponent from "@/components/child/actionBar/actionBar.vue";
import checkboxComponent from "@/components/child/form/checkbox.vue";
import comboComponent from "@/components/child/form/combo.vue";
import dataTooltipComponent from "@/components/child/form/datatooltip.vue";
import fieldsetComponent from "@/components/child/form/fieldset.vue";
import moedaComponent from "@/components/child/form/moeda.vue";
import logEntidadeComponent from "@/components/child/logEntidade/logEntidade.vue";
import RascunhoComponent from "@/components/child/rascunho/rascunho";
import rascunhoComponent from "@/components/child/rascunho/rascunho.vue";
import searchComboComponent from "@/components/child/searchCombo/search.vue";
import { Component, Prop, Watch } from "@/decorators";
import ClienteModel from "@/models/clienteModel";
import ConfiguracaoFranquiaModel from "@/models/configuracaoFranquia/configuracaoFranquiaModel";
import Configuracoes from "@/models/enum/configuracao/configuracoes";
import FinalidadeEmissao from "@/models/enum/finalidadeEmissao";
import IndicadorOperacao from "@/models/enum/indicadorOperacao";
import OrigemEmissao from "@/models/enum/origemEmissao";
import OrigemProdutoLote from "@/models/enum/origemProdutoLote";
import StatusEnvioNF from "@/models/enum/statusEnvioNF";
import TipoCadastro from "@/models/enum/tipoCadastro";
import TipoCliente from "@/models/enum/tipoCliente";
import TipoOperacao from "@/models/enum/tipoOperacao";
import TiposFranquia from "@/models/enum/tiposFranquia";
import TiposItemManipulacao from "@/models/enum/tiposItemManipulacao";
import TiposMovimentacao from "@/models/enum/tiposMovimentacao";
import FornecedorModel from "@/models/fornecedorModel";
import ManipulacaoOrdemModel from "@/models/manipulacaoOrdemModel";
import NaturezaOperacaoModel from "@/models/naturezaOperacaoModel";
import NotaFiscalEletronicaEmissaoItemModel from "@/models/notaFiscalEletronicaEmissaoItemModel";
import NotaFiscalEletronicaEmissaoModel, { Cobranca, Pagamento } from "@/models/notaFiscalEletronicaEmissaoModel";
import NotaFiscalEntradaItemModel from "@/models/notaFiscalEntradaItemModel";
import NotaFiscalEntradaModel from "@/models/notaFiscalEntradaModel";
import PaginationModel from "@/models/paginationModel";
import NcmModel from "@/models/produto/ncmModel";
import ProdutoModel from "@/models/produto/produtoModel";
import TipoVendaModel from "@/models/tipoVendaModel";
import TransportadoraModel from "@/models/transporte/transportadoraModel";
import UsuarioModel from "@/models/usuarioModel";
import VendaItemModel from "@/models/vendaItemModel";
import VendaModel from "@/models/vendaModel";
import VolumeModel from "@/models/volumeModel";
import ClienteService from "@/services/clienteService";
import estoqueService from "@/services/estoqueService";
import FornecedorService from "@/services/fornecedorService";
import naturezaOperacaoService from "@/services/naturezaOperacaoService";
import NotaFiscalEletronicaEmissaoService from "@/services/notaFiscal/notaFiscalEletronicaEmissaoService";
import NcmService from "@/services/produto/ncmService";
import produtoService from "@/services/produto/produtoService";
import tipoVendaService from "@/services/tipoVendaService";
import TransportadoraService from "@/services/transporte/transportadoraService";
import unidadeMedidaService from "@/services/unidadeMedidaService";
import VolumeService from "@/services/volumeService";
import { AppState, Getters, Mutations, SessionActions } from "@/store/store";
import arithmeticHelper from "@/utils/common/arithmeticHelper";
import { getClienteCombo, getFornecedorCombo } from "@/utils/common/combo/combotext";
import Delay from "@/utils/common/delay";
import Bus from "@/utils/events/bus";
import ValidationErrorWrapper from "@/wrappers/validationErrorWrapper";

import clienteComponent from "../../parent/crud/cliente/edit.vue";
import fornecedorComponent from "../../parent/crud/fornecedor/edit.vue";
import naturezaOperacaoComponent from "../../parent/crud/naturezaoperacao/edit.vue";
import ClienteEditComponent from "../crud/cliente/edit";
import FornecedorEditComponent from "../crud/fornecedor/edit";
import NaturezaOperacaoEditComponent from "../crud/naturezaoperacao/edit";
import ShortcutComponent from "../shortcut/shortcut";
import shortcutComponent from "../shortcut/shortcut.vue";

import cobrancaComponent from "./cobranca.vue";
import ExportacaoComponent from "./exportacao";
import exportacaoComponent from "./exportacao.vue";
import ItemNFEComponent from "./item";
import itemListComponent from "./item.vue";
import {
    carregaICMSInterestadual,
    getNaturezaOperacaoTipoVenda,
    getNewItemByProduto,
    loadImpostosByNaturezaOperacao,
    validateItemNota,
} from "./nfeUtils";
import pagamentoComponent from "./pagamento.vue";
import ResumoComponent from "./resumo/resumo";
import resumoComponent from "./resumo/resumo.vue";
import retencoesComponent from "./retencoes.vue";
import transportadoraComponent from "./transportadora.vue";
import volumesComponent from "./volumes.vue";

import "../crud/crud.scss";

type ComboOptions = {
    value: number;
    text: string;
};

@Component({
    components: {
        transportadoraComponent,
        volumesComponent,
        cobrancaComponent,
        pagamentoComponent,
        retencoesComponent,
        exportacaoComponent,
        itemListComponent,
        checkboxComponent,
        dataTooltipComponent,
        moedaComponent,
        fieldsetComponent,
        comboComponent,
        shortcutComponent,
        naturezaOperacaoComponent,
        clienteComponent,
        fornecedorComponent,
        resumoComponent,
        actionBarComponent,
        searchComboComponent,
        rascunhoComponent,
        logEntidadeComponent,
    },
    computed: {
        ...mapState({
            nota: (state: AppState) => state.nfeContext.nota,
            consulta: (state: AppState) => state.nfeContext.consulta,
            emissaoVenda: (state: AppState) => state.nfeContext.emissaoVenda,
            usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        }),
        ...mapGetters(["GET_CONFIG_FRANQUIA", "GET_IS_FRACIONAMENTO", "GET_IS_ESTEREIS"] as Getters),
    },
    methods: mapMutations([
        "SET_NOTA",
        "ADD_ITEM_NOTA",
        "UPDATE_ITEM",
        "SET_NOTA_CONSULTA",
        "SET_NOTA_EMISSAO_VENDA",
        "SET_NOTA_TRANSPORTE",
        "CONSOLIDA_NOTA",
    ] as Mutations),
})
export default class NfeEditComponent extends Vue {
    // State computed props
    nota: NotaFiscalEletronicaEmissaoModel;
    consulta: boolean;
    emissaoVenda: boolean;
    usuarioLogado: UsuarioModel;
    SET_NOTA: (nota: NotaFiscalEletronicaEmissaoModel) => void;
    ADD_ITEM_NOTA: ({
        item,
        simplesNacional,
    }: {
        item: NotaFiscalEletronicaEmissaoItemModel;
        simplesNacional: boolean;
    }) => void;
    UPDATE_ITEM: (data: object) => void;
    SET_NOTA_CONSULTA: (consulta: boolean) => void;
    SET_NOTA_EMISSAO_VENDA: (emissaoVenda: boolean) => void;
    SET_NOTA_TRANSPORTE: (notaTransporte: boolean) => void;
    CONSOLIDA_NOTA: (venda: VendaModel) => void;
    GET_CONFIG_FRANQUIA: (configuracao: Configuracoes) => ConfiguracaoFranquiaModel;
    GET_IS_FRACIONAMENTO: () => Promise<boolean>;
    GET_IS_ESTEREIS: () => Promise<boolean>;

    private validationErrorWrapper = new ValidationErrorWrapper(this.$validator);
    private itemListComponent: ItemNFEComponent = null;
    private exportacaoComponent: ExportacaoComponent = null;
    private service = new NotaFiscalEletronicaEmissaoService();
    private clienteService = new ClienteService();
    private fornecedorService = new FornecedorService();
    private transportadoraService = new TransportadoraService();

    private modelId = null;
    private vendaModel: VendaModel = null;
    private naturezaOperacaoComponent: NaturezaOperacaoEditComponent = null;
    private clienteComponent: ClienteEditComponent = null;
    private fornecedorComponent: FornecedorEditComponent = null;
    private shortcutComponent: ShortcutComponent = null;
    private resumoComponent: ResumoComponent = null;
    private ncmService = new NcmService();

    private tipoOperacaoOptions: Array<ComboOptions> = [
        { text: `${TipoOperacao.Entrada}-Entrada`, value: TipoOperacao.Entrada },
        { text: `${TipoOperacao.Saida}-Saida`, value: TipoOperacao.Saida },
    ];
    private finalidadeEmissaoOptions: Array<ComboOptions> = [
        { text: "1-NF-e Normal", value: FinalidadeEmissao.Normal },
        { text: "2-NF-e Complementar", value: FinalidadeEmissao.Complementar },
        { text: "3-NF-e de Ajuste", value: FinalidadeEmissao.Ajuste },
        { text: "4-Devolução de mercadoria", value: FinalidadeEmissao.Devolucao },
        { text: "5-Nota de Crédito", value: FinalidadeEmissao.NotaCredito },
        { text: "6-Nota de Débito", value: FinalidadeEmissao.NotaDebito },
    ];
    private modeloOptions: Array<ComboOptions> = [
        { text: "55-NF-e", value: 55 },
        { text: "59-CF-e", value: 59 },
        { text: "65-NFC-e", value: 65 },
    ];
    private indicadorOperacaoOptions: Array<ComboOptions> = [
        { text: "0-Normal", value: IndicadorOperacao.Normal },
        { text: "1-Consumidor final", value: IndicadorOperacao.ConsumidorFinal },
    ];
    private indicadorPresencaOptions: Array<ComboOptions> = [
        { text: "0-Não se aplica", value: 0 },
        { text: "1-Operação presencial", value: 1 },
        { text: "2-Operação não presencial, pela Internet", value: 2 },
        { text: "3-Operação não presencial, Teleatendimento", value: 3 },
        { text: "4-NFC-e em operação com entrega a domicílio", value: 4 },
        { text: "9-Operação não presencial, outros", value: 9 },
    ];
    private indicativoIntermedioOptions: Array<ComboOptions> = [
        { text: "0-Operação sem intermediador", value: 0 },
        { text: "1-Operação em site ou plataforma de terceiros", value: 1 },
    ];
    private tipoCadastroOptions: Array<ComboOptions> = [
        { text: `${TipoCadastro.Fornecedor}-Fornecedor`, value: TipoCadastro.Fornecedor },
        { text: `${TipoCadastro.Cliente}-Cliente`, value: TipoCadastro.Cliente },
    ];
    private tipoClienteOptions: Array<ComboOptions> = [
        { text: "Normal", value: TipoCliente.Normal },
        { text: "Franquia", value: TipoCliente.Franquia },
        { text: "Filial", value: TipoCliente.Filial },
    ];
    private cfopOptions: Array<ComboOptions> = [];
    private fornecedorOptions: Array<ComboOptions> = [];
    private estoqueOptions: Array<ComboOptions> = [];

    private loadingConsulta = false;
    private baixaEstoque = false;
    private clienteId = null;
    private notaTransporte = false;
    private clienteAtualTipoCliente: TipoCliente = null;
    private loadingNotaTransporte = false;
    private loadingNotaByVenda = false;
    private skipConfiguracaoFiscalReload = false;
    // Flag one-shot: suprime o reload automático do onNaturezaChange na única atribuição da
    // natureza do produto ao cabeçalho da transporte (essa natureza pode ter usaConfiguracaoFiscal
    // e o reload da transporte reentra em loop). Consumido pelo próprio onNaturezaChange.
    private skipReloadNaturezaProdutoTransporte = false;
    // Natureza cuja restrição de estoque já foi avisada. O onNaturezaChange é reexecutado várias
    // vezes para a mesma natureza (watcher, reload da configuração fiscal, load da consulta) e sem
    // esse controle o aviso de importação reaparece em cada passagem.
    private naturezaImportacaoAvisadaId: number | null = null;
    private isRascunho = false;
    private franquiaGrupoAtlas = false;

    @Prop(Boolean) isEmissaoVenda: boolean;
    @Prop({ type: Boolean, default: false }) isModal: boolean;

    private customComboTextCliente = getClienteCombo;

    private get franquiaFracionadora() {
        return this.usuarioLogado.franquia?.tipoFranquia == TiposFranquia.Fracionadora;
    }
    produtoService: any;

    private async onSaveRascunho() {
        const rascunhoComponent = this.$refs.rascunhoComponent as RascunhoComponent;
        rascunhoComponent.saveRascunho(this.nota);
    }

    private async onLoadRascunho(data: any) {
        this.isRascunho = true;
        this.SET_NOTA(data);
        await Delay(1000);
        this.SET_NOTA(data);
        await Delay(1000);
    }

    public async loadById(id: number) {
        this.modelId = id;
        this.SET_NOTA_EMISSAO_VENDA(false);

        await this.load();
    }

    private async load() {
        this.SET_NOTA_EMISSAO_VENDA(this.isEmissaoVenda);
        this.SET_NOTA_TRANSPORTE(false);
        this.nota.movimentarEstoque = false;

        this.nota.estoqueId = this.GET_CONFIG_FRANQUIA(Configuracoes.EstoquePadrao)?.estoqueId;

        if (!this.emissaoVenda && this.modelId) {
            this.SET_NOTA_CONSULTA(true);
            this.loadingConsulta = true;

            const data = await this.service.get(this.modelId).resolveWithJSON<NotaFiscalEletronicaEmissaoModel>();
            const destinatarioId = data.destinatarioId;
            const naturezaOperacaoId = data.naturezaOperacaoId;
            const movimentarEstoque = data.movimentarEstoque;
            this.SET_NOTA(data);

            await this.carregarCfops();
            this.nota.destinatarioId = destinatarioId;
            this.nota.naturezaOperacaoId = naturezaOperacaoId;
            await this.onNaturezaChange();
            this.nota.destinatarioId = destinatarioId;
            this.nota.naturezaOperacaoId = naturezaOperacaoId;
            this.nota.movimentarEstoque = movimentarEstoque;
            this.SET_NOTA(this.nota);
            this.loadingConsulta = false;
        } else {
            this.clear();
        }

        this.validationErrorWrapper.clearErrors();
    }

    public async loadNotaDevolucaoCompra(nota: NotaFiscalEntradaModel, itens: NotaFiscalEntradaItemModel[]) {
        this.clear();

        this.nota.finalidadeEmissao = 4;
        this.nota.vendaId = null;
        this.nota.chaveReferenciada = nota.chave;
        this.nota.tipoOperacao = TipoOperacao.Saida;
        this.nota.tipoCadastro = TipoCadastro.Fornecedor;

        this.nota.destinatarioId = nota.fornecedorId;
        const naturezaOperacao = await this.getNaturezaDevolucaoByFornecedor(nota.fornecedorId);
        this.nota.naturezaOperacaoId = naturezaOperacao?.id;
        this.nota.transportadoraId = nota.transportadoraId;
        this.nota.modalidadeFrete = nota.transportadoraId ? 0 : 9;

        this.nota.itens = [];

        this.nota.destinatario = await this.fornecedorService.get(nota.fornecedorId).resolveWithJSON<FornecedorModel>();

        for await (const itemDevolver of itens) {
            let item = new NotaFiscalEletronicaEmissaoItemModel();
            const itemDefault = await getNewItemByProduto(
                itemDevolver.produtoId,
                this.nota,
                this.usuarioLogado.franquia,
            );

            item.updateFrom(itemDevolver);
            item.ncmId = itemDefault.ncmId;
            item.ncmCodigo = itemDefault.ncmCodigo;
            item.quantidade = itemDevolver.quantidadeADevolver;
            item.produtoLoteId = itemDevolver.produtoLoteId;

            if (!item.unidadeMedidaId) item.unidadeMedidaId = itemDefault.unidadeMedidaId;
            if (!item.EAN) item.EAN = Number(item.EAN);

            if (!item.origemMercadoria) item.origemMercadoria = itemDefault.origemMercadoria;
            if (!item.aliquotaICMS) item.aliquotaICMS = itemDefault.aliquotaICMS;
            if (!item.percentualDiferimento) item.percentualDiferimento = itemDefault.percentualDiferimento;
            if (!item.percentualReducaoBase) item.percentualReducaoBase = itemDefault.percentualReducaoBase;
            if (!item.cstICMS) item.cstICMS = itemDefault.cstICMS;

            item.comICMSPartilha = itemDefault.comICMSPartilha;
            if (!item.aliquotaFCPUFDestino) item.aliquotaFCPUFDestino = itemDefault.aliquotaFCPUFDestino;
            if (!item.aliquotaUFDestino) item.aliquotaUFDestino = itemDefault.aliquotaUFDestino;
            if (!item.aliquotaInterestadual) item.aliquotaInterestadual = itemDefault.aliquotaInterestadual;
            if (!item.aliquotaInterestadualPartilha) {
                item.aliquotaInterestadualPartilha = itemDefault.aliquotaInterestadualPartilha;
            }

            if (!item.sujeitoIPI) item.sujeitoIPI = itemDefault.sujeitoIPI;
            if (!item.aliquotaIPI) item.aliquotaIPI = itemDefault.aliquotaIPI;
            if (!item.cstIPI) item.cstIPI = itemDefault.cstIPI;
            if (!item.percentualDevolvido) item.percentualDevolvido = itemDefault.percentualDevolvido;
            if (!item.valorIPIDevolvido) item.valorIPIDevolvido = itemDefault.valorIPIDevolvido;

            if (!item.aliquotaPIS) item.aliquotaPIS = itemDefault.aliquotaPIS;
            if (!item.cstPIS) item.cstPIS = itemDefault.cstPIS;

            if (!item.aliquotaCOFINS) item.aliquotaCOFINS = itemDefault.aliquotaCOFINS;
            if (!item.cstCOFINS) item.cstCOFINS = itemDefault.cstCOFINS;

            if (!item.valorDesconto) item.valorDesconto = 0;
            if (!item.valorFrete) item.valorFrete = 0;
            if (!item.valorOutraDespesa) item.valorOutraDespesa = 0;
            if (!item.valorSeguro) item.valorSeguro = 0;

            if (itemDevolver.valorIPI > 0) {
                item.percentualDevolvido = arithmeticHelper.round(
                    (itemDevolver.quantidadeADevolver / itemDevolver.quantidade) * 100,
                );
                item.valorIPIDevolvido = arithmeticHelper.round(
                    (itemDevolver.valorIPI * item.percentualDevolvido) / 100,
                );
            }

            const IPI = itemDevolver.notaFiscalEntradaItemImpostos.filter(i => i.imposto == "IPI");
            if (IPI.length > 0 && IPI[0].valorImposto > 0) {
                item.percentualDevolvido = arithmeticHelper.round(
                    (itemDevolver.quantidadeADevolver / itemDevolver.quantidade) * 100,
                );
                item.valorIPIDevolvido = arithmeticHelper.round((IPI[0].valorImposto * item.percentualDevolvido) / 100);
            }

            if (item.produtoLoteId) {
                const data = await this.itemListComponent.getProdutoLoteDados(item.produtoLoteId, item, true);
                item = data.item;
            }
            item.unidadeMedidaSigla = this.itemListComponent.getUnidadeMedidaSigla(item.unidadeMedidaId);

            this.ADD_ITEM_NOTA({ item, simplesNacional: this.usuarioLogado.franquia.opcaoSimplesNacional });
        }
    }

    public async loadModelByVenda(venda: VendaModel, isReloading = false) {
        this.clear();

        this.vendaModel = venda;

        this.loadingNotaByVenda = true;

        this.nota.tipoOperacao = TipoOperacao.Saida;
        this.nota.tipoCadastro = TipoCadastro.Cliente;
        this.nota.vendaId = venda.id;
        this.nota.destinatarioId = venda.clienteId;
        this.clienteId = venda.clienteId;

        const cliente = await this.clienteService.get(venda.clienteId).then(r => r.json() as Promise<ClienteModel>);

        if (!isReloading) {
            const outroEstado = this.usuarioLogado.franquia.estadoId !== cliente.estadoId;

            const naturezaCliente = outroEstado
                ? cliente.naturezaOperacaoForaEstadoId
                : cliente.naturezaOperacaoDentroEstadoId;

            if (naturezaCliente) {
                this.nota.naturezaOperacaoId = naturezaCliente;
            } else {
                const naturezaOperacao = await this.getNaturezaByCliente(venda.clienteId);
                this.nota.naturezaOperacaoId = naturezaOperacao?.id;
            }
        }
        this.nota.transportadoraId = venda.transportadoraId;
        this.nota.itens = [];

        this.nota.destinatario = cliente;

        if (cliente.indicadorOperacao != null) {
            this.nota.indicadorOperacao = cliente.indicadorOperacao;
        }

        this.baixaEstoque = await this.perguntaBaixaEstoque(cliente);

        try {
            venda.contaReceber.movimentacoes.forEach(pag => {
                let meioPagamento = 0;
                switch (pag.formaPagamentoDescricao) {
                    case "Dinheiro":
                        meioPagamento = 1;
                        break;
                    case "Cheque":
                        meioPagamento = 2;
                        break;
                    case "Boleto":
                        meioPagamento = 15;
                        break;
                    case "Cartão de Débito":
                        meioPagamento = 4;
                        break;
                    case "Cartão de Crédito":
                        meioPagamento = 3;
                        break;
                    default:
                        meioPagamento = 1;
                        break;
                }
                this.nota.pagamentos.push({ indicadorPagamento: 0, meioPagamento, valorPagamento: pag.valor });
            });

            if (venda.contaReceber.lancamentosVinculados.length > 0) {
                const cobranca = this.nota.cobranca;
                this.nota.cobranca = new Cobranca();
                this.nota.cobranca.numero = "1";
                venda.contaReceber.lancamentosVinculados.forEach((parcela, index) => {
                    this.nota.cobranca.duplicatas.push({
                        numero: (index + 1).toString(),
                        dataVencimento: parcela.dataVencimento,
                        valor: parcela.valorTotal,
                    });
                });
                const totalDuplicatas = this.nota.cobranca.duplicatas.reduce((acc, p) => acc + p.valor, 0);
                this.nota.cobranca.valorOriginal = totalDuplicatas + this.nota.cobranca.valorDesconto;
                this.nota.cobranca.valorLiquido = cobranca.valorOriginal - cobranca.valorDesconto;
            }

            if (venda.volumeId) {
                const volume = await new VolumeService()
                    .get(venda.volumeId)
                    .then(r => r.json() as Promise<VolumeModel>);
                if (volume) {
                    this.nota.volumes.push(volume);
                }
            }
        } catch {}

        const produtoAlteradosCompra = [];

        for (let index = 0; index < venda.itens.length; index++) {
            const itemVenda = venda.itens[index];

            if (itemVenda.fracionamento) {
                for (let i = 0; i < itemVenda.fracionamento.itens.length; i++) {
                    let item = await getNewItemByProduto(
                        itemVenda.fracionamento.itens[i].produtoId,
                        this.nota,
                        this.usuarioLogado.franquia,
                        this.baixaEstoque,
                        produtoAlteradosCompra,
                    );
                    item.produtoId = itemVenda.fracionamento.itens[i].produtoId;
                    item.produtoLoteId = itemVenda.fracionamento.itens[i].produtoLoteId;
                    item.quantidade = itemVenda.fracionamento.itens[i].quantidade;
                    item.valorUnitario = itemVenda.fracionamento.itens[i].valor;
                    item.valorDesconto = itemVenda.desconto;
                    item.valorOutraDespesa = itemVenda.acrescimo;
                    item.valorTotal = itemVenda.valorTotal;
                    item.unidadeMedidaSigla = this.itemListComponent.getUnidadeMedidaSigla(item.unidadeMedidaId);

                    if (item.produtoLoteId) {
                        const data = await this.itemListComponent.getProdutoLoteDados(item.produtoLoteId, item);
                        item = data.item;

                        if (data.lote?.origemProdutoLote == OrigemProdutoLote.Fracionamento) {
                            const unidadeMedidaFracionado = this.itemListComponent.getUnidadeMedidaSigla(
                                data.lote.unidadeMedidaFracionadoId,
                            );
                            item.produtoDescricao += ` ${data.lote.volumeFracionado} ${unidadeMedidaFracionado}`;

                            item.unidadeMedidaId = this.itemListComponent.getIdUnidadeMedidaUnidade();
                            item.unidadeMedidaSigla = "UN";
                        }
                    }

                    if (venda.utilizaValorCusto) {
                        item = this.setValoresTransferencia(item, itemVenda);
                    }

                    this.ADD_ITEM_NOTA({ item, simplesNacional: this.usuarioLogado.franquia.opcaoSimplesNacional });
                }
            } else if (itemVenda.manipulacaoOrdem) {
                const produtoConfiguracao = this.GET_CONFIG_FRANQUIA(Configuracoes.ProdutoPadraoDeManipulacao);

                let item = await getNewItemByProduto(
                    produtoConfiguracao.valor,
                    this.nota,
                    this.usuarioLogado.franquia,
                    this.baixaEstoque,
                    produtoAlteradosCompra,
                );

                item.produtoId = produtoConfiguracao.valor;
                item.quantidade = 1;
                item.valorDesconto = itemVenda.manipulacaoOrdem.desconto;
                item.valorOutraDespesa = itemVenda.manipulacaoOrdem.acrescimo;
                item.valorUnitario = itemVenda.manipulacaoOrdem.valorTotal;
                item.valorTotal = itemVenda.manipulacaoOrdem.valorTotal;

                if (venda.utilizaValorCusto) {
                    item = this.setValoresTransferencia(item, itemVenda);
                }
                item.unidadeMedidaSigla = this.itemListComponent.getUnidadeMedidaSigla(item.unidadeMedidaId);

                this.ADD_ITEM_NOTA({ item, simplesNacional: this.usuarioLogado.franquia.opcaoSimplesNacional });
            } else {
                let item = await getNewItemByProduto(
                    itemVenda.produtoId,
                    this.nota,
                    this.usuarioLogado.franquia,
                    this.baixaEstoque,
                    produtoAlteradosCompra,
                );
                item.produtoId = itemVenda.produtoId;
                item.quantidade = itemVenda.quantidade;
                item.produtoLoteId = itemVenda.produtoLoteId;
                item.valorDesconto = itemVenda.desconto;
                item.valorOutraDespesa = itemVenda.acrescimo;
                item.valorUnitario = itemVenda.valorUnitario;
                item.valorTotal = itemVenda.valorTotal;

                if (item.produtoLoteId) {
                    const data = await this.itemListComponent.getProdutoLoteDados(item.produtoLoteId, item);
                    item = data.item;
                }
                item.unidadeMedidaSigla = this.itemListComponent.getUnidadeMedidaSigla(item.unidadeMedidaId);

                if (venda.utilizaValorCusto) {
                    item = this.setValoresTransferencia(item, itemVenda);
                }
                this.ADD_ITEM_NOTA({ item, simplesNacional: this.usuarioLogado.franquia.opcaoSimplesNacional });
            }
        }

        this.CONSOLIDA_NOTA(venda);

        this.loadingNotaByVenda = false;

        if (this.baixaEstoque) {
            await this.showQtdItensAlteradosCompra(produtoAlteradosCompra);
        }
    }

    public async loadModelByTransporte(venda: VendaModel, isReloading = false) {
        this.clear();
        const descriminarAcrescimo = this.GET_CONFIG_FRANQUIA(
            Configuracoes.DescriminarAcrescimoNotaTransporte,
        )?.verdadeiro;

        this.loadingNotaTransporte = true;

        this.notaTransporte = true;
        this.SET_NOTA_TRANSPORTE(true);

        const cliente = await this.clienteService.get(venda.clienteId).resolveWithJSON<ClienteModel>();
        this.clienteAtualTipoCliente = cliente.tipoCliente;
        this.nota.destinatario = cliente;

        await this.carregarCfops();

        this.vendaModel = venda;
        this.nota.tipoOperacao = TipoOperacao.Saida;
        this.nota.tipoCadastro = TipoCadastro.Cliente;
        this.nota.vendaId = venda.id;
        this.nota.destinatarioId = venda.clienteId;
        this.clienteId = venda.clienteId;
        this.nota.transportadoraId = venda.transportadoraId;
        this.nota.itens = [];

        if (cliente.indicadorOperacao != null) {
            this.nota.indicadorOperacao = cliente.indicadorOperacao;
        }

        const unidadeMedida = await unidadeMedidaService.comboCached(this.usuarioLogado.franquiaId);

        if (venda.contaReceber.lancamentosVinculados.length > 0) {
            this.nota.cobranca = new Cobranca();
            this.nota.cobranca.numero = "1";
            venda.contaReceber.lancamentosVinculados.forEach((parcela, index) => {
                this.nota.cobranca.duplicatas.push({
                    numero: (index + 1).toString(),
                    dataVencimento: parcela.dataVencimento,
                    valor: parcela.valorTotal,
                });
            });
            const totalDuplicatas = this.nota.cobranca.duplicatas.reduce((acc, p) => acc + p.valor, 0);
            this.nota.cobranca.valorOriginal = totalDuplicatas + this.nota.cobranca.valorDesconto;
            this.nota.cobranca.valorLiquido = this.nota.cobranca.valorOriginal - this.nota.cobranca.valorDesconto;
        }

        const outroEstado = this.usuarioLogado.franquia.estadoId !== cliente.estadoId;

        // Precisa viver fora do bloco !isReloading para ser reaproveitada no loop de itens abaixo,
        // garantindo que os itens usem a mesma variante "consumidor final" resolvida no cabeçalho.
        let aplicaCFOPConsumidorFinal = false;

        if (!isReloading) {
            const tipoVenda = venda.tipoVendaId
                ? await tipoVendaService.get(venda.tipoVendaId).catch(() => null as TipoVendaModel | null)
                : null;

            // O cabeçalho segue a mesma ordem de prioridade do item (cliente → produto →
            // tipoVenda → CFOP padrão) para não divergir do CFOP dos itens. Como o cabeçalho é
            // único, usa-se como representativo o produto do item de maior valor em reais da venda.
            const produtoIdRepresentativo = this.getProdutoIdRepresentativoVenda(venda.itens);
            const produto = produtoIdRepresentativo
                ? await produtoService
                      .getById(produtoIdRepresentativo, 0.01, false)
                      .resolveWithJSON<ProdutoModel>()
                      .catch(() => null as ProdutoModel | null)
                : null;

            const naturezaProduto = outroEstado
                ? produto?.naturezaOperacaoForaEstadoId
                : produto?.naturezaOperacaoDentroEstadoId;
            const naturezaCliente = outroEstado
                ? cliente.naturezaOperacaoForaEstadoId
                : cliente.naturezaOperacaoDentroEstadoId;
            const naturezaTipoVenda = getNaturezaOperacaoTipoVenda(
                tipoVenda,
                outroEstado,
                this.franquiaFracionadora,
            );

            if (naturezaProduto) {
                // Suprime o reload automático só nesta atribuição (a natureza do produto pode ter
                // usaConfiguracaoFiscal e reentrar em loop na transporte). Os itens já aplicam a
                // configuração fiscal via loadImpostos, então o reload seria redundante aqui.
                if (this.nota.naturezaOperacaoId !== naturezaProduto) {
                    this.skipReloadNaturezaProdutoTransporte = true;
                }
                this.nota.naturezaOperacaoId = naturezaProduto;
            } else if (naturezaCliente) {
                this.nota.naturezaOperacaoId = naturezaCliente;
            } else if (naturezaTipoVenda) {
                this.nota.naturezaOperacaoId = naturezaTipoVenda;
            } else {
                const naturezaOperacao = await this.getNaturezaByCodigo(outroEstado ? 6949 : 5949);
                this.nota.naturezaOperacaoId = naturezaOperacao?.id;
            }

            // Apenas caso o cliente seja cadastrado no estado do Paraná (ID 41)
            aplicaCFOPConsumidorFinal =
                cliente.estadoId == 41 &&
                cliente.indicadorOperacao == IndicadorOperacao.ConsumidorFinal &&
                ((await this.GET_IS_ESTEREIS()) || (await this.GET_IS_FRACIONAMENTO()));

            if (aplicaCFOPConsumidorFinal && this.nota.naturezaOperacaoId != null) {
                this.nota.naturezaOperacaoId = await this.resolverCFOPConsumidorFinal(this.nota.naturezaOperacaoId);
            }
        }

        try {
            if (this.usuarioLogado.franquia.tipoFranquia == TiposFranquia.Estereis && this.nota.volumes.length == 0) {
                let volumeModel = new VolumeModel();
                if (venda.volumeId) {
                    volumeModel = await new VolumeService().get(venda.volumeId).resolveWithJSON<VolumeModel>(false);
                }

                const volume = new VolumeModel();
                volume.quantidade = volumeModel.quantidade ?? 1;
                volume.numeracao = volumeModel.numeracao ?? "1";
                volume.pesoLiquido = volumeModel.pesoLiquido ?? 4;
                volume.pesoBruto = volumeModel.pesoBruto ?? null;
                volume.especie = volumeModel.especie ?? "";
                volume.marca = volumeModel.marca ?? "";

                this.nota.volumes.push(volume);
            } else if (venda.volumeId) {
                const volume = await new VolumeService().get(venda.volumeId).resolveWithJSON<VolumeModel>(false);
                if (volume) {
                    this.nota.volumes.push(volume);
                }
            }
        } catch {}

        for (const itemVenda of venda.itens) {
            if (itemVenda.fracionamento) {
                for (let i = 0; i < itemVenda.fracionamento.itens.length; i++) {
                    let item = new NotaFiscalEletronicaEmissaoItemModel();

                    item.produtoId = itemVenda.fracionamento.itens[i].produtoId;
                    item.produtoLoteId = itemVenda.fracionamento.itens[i].produtoLoteId;
                    item.quantidade = itemVenda.fracionamento.itens[i].quantidade;
                    item.valorUnitario = itemVenda.fracionamento.itens[i].valor;
                    item.valorDesconto = itemVenda.desconto;
                    item.valorOutraDespesa = itemVenda.acrescimo;
                    item.valorTotal = itemVenda.valorTotal;
                    item.produtoDescricao =
                        itemVenda.fracionamento.itens[i].descricao != null
                            ? itemVenda.fracionamento.itens[i].descricao
                            : itemVenda.fracionamento.itens[i].produto.descricaoFiscal ??
                              itemVenda.fracionamento.itens[i].produto.descricao;

                    try {
                        if (itemVenda.fracionamento.itens[i].produto.ncmId) {
                            const ncm = await this.ncmService
                                .get(itemVenda.fracionamento.itens[i].produto.ncmId)
                                .resolveWithJSON<NcmModel>(false, false);

                            item.ncmId = ncm?.id;
                            item.ncmCodigo = ncm?.codigo;
                        }
                    } catch {}

                    if (item.produtoLoteId) {
                        await this.itemListComponent.getProdutoLoteDados(item.produtoLoteId, item);
                    }

                    item.unidadeMedidaSigla = "UN";
                    item.unidadeMedidaId = unidadeMedida.find(x => x.text == "un")?.value;
                    item.cstIBSCBS = itemVenda.fracionamento.itens[i].produto?.cstIBSCBS;
                    item.classificacaoTributaria = itemVenda.fracionamento.itens[i].produto?.classificacaoTributaria;

                    if (venda.utilizaValorCusto || !descriminarAcrescimo) {
                        item = this.setValoresTransferencia(item, itemVenda);
                    }

                    item = await loadImpostosByNaturezaOperacao(
                        item,
                        this.nota.naturezaOperacaoId!,
                        cliente.id,
                        this.vendaModel.tipoVendaId,
                        outroEstado,
                        false,
                        this.franquiaFracionadora,
                    );

                    item = await this.aplicarCFOPConsumidorFinalNoItem(item, aplicaCFOPConsumidorFinal, outroEstado);

                    this.ADD_ITEM_NOTA({ item, simplesNacional: this.usuarioLogado.franquia.opcaoSimplesNacional });
                }
            } else {
                let item = new NotaFiscalEletronicaEmissaoItemModel();
                let produto = itemVenda.produto;

                if (itemVenda.manipulacaoOrdem) {
                    if (!itemVenda.manipulacaoOrdem.descricao) {
                        itemVenda.manipulacaoOrdem.descricao = itemVenda.produtoDescricao;
                    }
                    item.produtoDescricao = await this.getDescricaoFiscal(itemVenda.manipulacaoOrdem);
                    item.valorDesconto = itemVenda.manipulacaoOrdem.desconto;
                    item.valorOutraDespesa = itemVenda.manipulacaoOrdem.acrescimo;
                    item.valorUnitario =
                        (itemVenda.manipulacaoOrdem.valorTotal -
                            itemVenda.manipulacaoOrdem.acrescimo +
                            itemVenda.manipulacaoOrdem.desconto) /
                        itemVenda.quantidade;
                    item.valorTotal = itemVenda.manipulacaoOrdem.valorTotal;

                    const manipulacaoItem =
                        itemVenda.manipulacaoOrdem.itens.find(p => p.ativoPrincipal) ??
                        itemVenda.manipulacaoOrdem.itens.find(p => p.tipoItem == TiposItemManipulacao.MateriaPrima);

                    if (!manipulacaoItem) {
                        throw Error(this.$t("__.ts.ativoPrincipalNaoDefinido") as string);
                    }

                    item.produtoId = manipulacaoItem.produtoId;
                    item.produtoLoteId = manipulacaoItem.produtoLoteId!;
                    produto = manipulacaoItem.produto;
                } else {
                    item.produtoDescricao = itemVenda?.produto?.descricaoFiscal ?? itemVenda.produtoDescricao;
                    item.valorDesconto = itemVenda.desconto;
                    item.valorOutraDespesa = itemVenda.acrescimo;
                    item.valorUnitario = itemVenda.valorUnitario;
                    item.valorTotal = itemVenda.valorTotal;
                    item.produtoId = itemVenda.produtoId;
                    item.produtoLoteId = itemVenda.produtoLoteId;
                }

                if (venda.utilizaValorCusto || !descriminarAcrescimo) {
                    item = this.setValoresTransferencia(item, itemVenda);
                }

                item.quantidade = itemVenda.quantidade;
                item.unidadeMedidaSigla = "UN";

                try {
                    if (item.produtoId && !produto) {
                        produto = await produtoService
                            .getById(item.produtoId)
                            .withLoading()
                            .resolveWithJSON<ProdutoModel>();
                    }

                    item.cstIBSCBS = produto.cstIBSCBS;
                    item.classificacaoTributaria = produto.classificacaoTributaria;

                    if (produto?.ncmId) {
                        const ncm = await this.ncmService.get(produto?.ncmId).resolveWithJSON<NcmModel>(false, false);

                        item.ncmId = ncm?.id;
                        item.ncmCodigo = ncm?.codigo;
                    }
                    await this.itemListComponent.getProdutoLoteDados(item.produtoLoteId, item);
                } catch {}
                item.unidadeMedidaId = unidadeMedida.find(x => x.text == "un")?.value;

                item = await loadImpostosByNaturezaOperacao(
                    item,
                    this.nota.naturezaOperacaoId!,
                    cliente.id,
                    this.vendaModel.tipoVendaId,
                    outroEstado,
                    false,
                    this.franquiaFracionadora,
                );

                item = await this.aplicarCFOPConsumidorFinalNoItem(item, aplicaCFOPConsumidorFinal, outroEstado);

                this.ADD_ITEM_NOTA({ item, simplesNacional: this.usuarioLogado.franquia.opcaoSimplesNacional });
            }
        }

        if (this.nota.vendaInterestadual) {
            await this.onLoadICMSInterestadual();
        }

        this.CONSOLIDA_NOTA(venda);

        this.loadingNotaTransporte = false;
    }

    // Produto que representa a venda na definição do CFOP padrão do cabeçalho: o do item de maior valor
    // em reais e, no empate de valor, o do item de maior quantidade. Só produto acabado tem CFOP no
    // cadastro, então itens de manipulação ficam fora da escolha.
    private getProdutoIdRepresentativoVenda(itens: Array<VendaItemModel>) {
        const itensOrdenados = itens
            .filter(itemVenda => itemVenda.produtoId)
            .sort((a, b) => (b.valorTotal ?? 0) - (a.valorTotal ?? 0) || (b.quantidade ?? 0) - (a.quantidade ?? 0));

        return itensOrdenados[0]?.produtoId ?? null;
    }

    private setValoresTransferencia(item: NotaFiscalEletronicaEmissaoItemModel, itemVenda: VendaItemModel) {
        item.valorDesconto = 0;
        item.valorOutraDespesa = 0;
        item.valorUnitario = arithmeticHelper.round(itemVenda.valorTotal / itemVenda.quantidade, 4);
        item.valorTotal = itemVenda.valorTotal;
        return item;
    }

    private async getDescricaoFiscal(manipulacao: ManipulacaoOrdemModel) {
        if (manipulacao.itens.length > 0) {
            let produto = manipulacao.itens[0].produto;
            if (!produto) {
                produto = await produtoService
                    .getById(manipulacao.itens[0].produtoId)
                    .withLoading()
                    .resolveWithJSON<ProdutoModel>();
            }

            if (produto && produto.descricaoFiscal) {
                return `${manipulacao.formaFarmaceuticaDescricao ?? manipulacao.formaFarmaceutica?.descricao} Ativo (${
                    produto?.descricaoFiscal
                })`;
            }
        }

        return manipulacao.descricao;
    }

    private async getNaturezaByCliente(clienteId: number) {
        return await naturezaOperacaoService
            .getNaturezaByCliente(clienteId)
            .resolveWithJSON<NaturezaOperacaoModel>(false);
    }

    private async getNaturezaByCodigo(codigo: number) {
        return await naturezaOperacaoService.getNaturezaByCodigo(codigo).resolveWithJSON<NaturezaOperacaoModel>(false);
    }

    private async getNaturezaDevolucaoByFornecedor(fornecedorId: number) {
        try {
            return await naturezaOperacaoService
                .getNaturezaDevolucaoByFornecedor(fornecedorId)
                .resolveWithJSON<NaturezaOperacaoModel>(false);
        } catch {
            return null;
        }
    }

    private async validateNFE() {
        let isValid = await this.$validator.validateAll();

        const isIndustria = (await this.GET_IS_FRACIONAMENTO()) || (await this.GET_IS_ESTEREIS());
        if (isIndustria && this.nota.itens.some(i => !i.ncmId)) {
            this.$showWarning(this.$t("__.ts.atencao"), this.$t("__.Components.parent.nfe.edit_vue_html.itensSemNCM"));
            return false;
        }

        if (this.nota.notaExportacao && isValid) {
            if (!this.exportacaoComponent) {
                this.exportacaoComponent = this.$refs.exportacaoComponent as ExportacaoComponent;
            }

            isValid = await this.exportacaoComponent.validate();
        }

        if (isValid) {
            for (let index = 0; index < this.nota.itens.length; index++) {
                const element = this.nota.itens[index];
                const message = validateItemNota(element, this.nota, this.usuarioLogado.franquia.opcaoSimplesNacional);

                if (message != "") {
                    this.$showWarning("NF-e", `Item ${index + 1} - ${element.produtoDescricao} \n ${message}`);
                    return false;
                }
            }
            return true;
        }
        this.$focusErrorField();
        return false;
    }

    private clear() {
        this.nota.tipoOperacao = TipoOperacao.Saida;
        this.nota.tipoCadastro = TipoCadastro.Cliente;
        this.nota.modelo = 55;
        this.nota.finalidadeEmissao = 1;
        this.nota.indicadorPresenca = 0;
        this.nota.indicadorOperacao = 0;
        this.nota.itens = new Array<NotaFiscalEletronicaEmissaoItemModel>();
        this.nota.pagamentos = new Array<Pagamento>();
        this.nota.volumes = new Array<VolumeModel>();
        this.nota.destinatarioId = null;
        this.clienteId = null;
        this.clienteAtualTipoCliente = null;
        this.SET_NOTA_TRANSPORTE(false);
    }

    public async emitir(emitirByVenda = true): Promise<NotaFiscalEletronicaEmissaoModel> {
        if (await this.validateNFE()) {
            await this.checkEmailTransportadora();
            try {
                let response = null;
                if (emitirByVenda) {
                    response = await this.service.emitirByVenda(this.nota);
                } else {
                    response = await this.service.emitir(this.nota);
                }

                if (!response.ok) {
                    const error = await response.json();
                    throw error;
                }

                this.nota.id = (await response.json()) as number;
            } catch (err) {
                if (err) {
                    try {
                        await this.validationErrorWrapper.showErrorsAsync(err.length ? err : [err]);
                    } catch {
                        await this.$showError(
                            "Erro ao emitir nota",
                            "Ocorreu um erro inesperado ao emitir a nota fiscal. Tente novamente ou contate o suporte.",
                        );
                    }
                }
                return null;
            }

            // A nota já foi emitida no backend a partir daqui: uma falha nesta etapa não é
            // falha de emissão, só o status não pôde ser confirmado — nunca reportar como erro.
            try {
                const nota = await this.service.get(this.nota.id).resolveWithJSON<NotaFiscalEletronicaEmissaoModel>();

                if (nota.statusEnvio == StatusEnvioNF.Pendente) {
                    this.$showWarning(
                        "Enviado!",
                        "Nota Fiscal foi enviada mas ainda não obteve retorno. Consulte a emissão da nota novamente.",
                    );
                } else {
                    this.$showSuccess("Enviado!", "Nota Fiscal foi enviada com sucesso.");
                }
            } catch {
                this.$showWarning(
                    "Enviado!",
                    "Nota Fiscal foi enviada, mas não foi possível confirmar o status. Consulte a emissão da nota novamente.",
                );
            }

            return this.nota;
        }
    }

    private async save() {
        if (await this.validateNFE()) {
            await this.checkEmailTransportadora();
            let sucesso: boolean;
            try {
                sucesso = await this.service.emitir(this.nota).withLoading().resolveWithoutJSON();
            } catch {
                await this.$showError(
                    "Erro ao salvar NF-e",
                    "Ocorreu um erro inesperado ao salvar a nota fiscal. Tente novamente ou contate o suporte.",
                );
                return;
            }

            if (sucesso) {
                await this.$showSuccess("NF-e", "NF-e enviada com sucesso.");

                this.$router.back();
            }
        }
    }

    private async checkEmailTransportadora() {
        if (this.nota.transportadoraId) {
            const transportadora = await this.transportadoraService
                .get(this.nota.transportadoraId)
                .resolveWithJSON<TransportadoraModel>();

            if (transportadora.email) {
                if (
                    transportadora.enviaNotaFiscalEmail ||
                    (await this.$showQuestion("NF-e", "Deseja enviar nota para o email da transportadora?"))
                ) {
                    this.nota.enviaNotaFiscalEmail = true;
                    this.nota.emailTransportadora = transportadora.email;
                }
            }
        }
    }

    private cancel() {
        this.$router.back();
    }

    @Watch("nota")
    private onChangeNota() {
        if (!this.consulta) {
            this.SET_NOTA(this.nota);
        }
    }

    // @ts-ignore
    @Watch("nota.naturezaOperacaoId")
    private async onCFOPChange() {
        if (!this.nota.naturezaOperacaoId) {
            this.naturezaImportacaoAvisadaId = null;
            return;
        }

        if (this.isRascunho) {
            this.isRascunho = false;
            return;
        }

        await this.onNaturezaChange().withLoading();
    }

    // @ts-ignore
    @Watch("nota.vendaInterestadual")
    private async onChangeVendaInterestadual() {
        if (this.nota.vendaInterestadual) {
            await this.onLoadICMSInterestadual();
        }
    }

    private async onLoadICMSInterestadual() {
        for (let index = 0; index < this.nota.itens.length; index++) {
            const item = this.nota.itens[index];
            await carregaICMSInterestadual(item, this.nota, this.usuarioLogado.franquia);
            this.UPDATE_ITEM({ item, index });
        }
    }

    private async onNaturezaChange() {
        if (this.nota.naturezaOperacaoId) {
            // Os flags de supressão são lidos antes de qualquer await. O watcher da natureza roda no
            // tick seguinte à reatribuição do id, mas só alcançaria o if do reload depois das
            // requisições abaixo — quando o finally já teria liberado o flag e o reload reentraria
            // em loop.
            const skipConfiguracaoFiscal = this.skipConfiguracaoFiscalReload;
            // Consome o flag one-shot: quando o cabeçalho recebeu a natureza do produto na carga
            // da transporte, pula o reload desta vez (evita o loop). Só suprime uma passagem.
            const skipReloadProduto = this.skipReloadNaturezaProdutoTransporte;
            this.skipReloadNaturezaProdutoTransporte = false;

            // A natureza pode estar cadastrada em outra franquia (ex.: natureza herdada do produto na
            // nota de transporte). Resolve a equivalente por código na franquia atual; sem equivalente,
            // não há configuração fiscal a aplicar — segue sem quebrar.
            const data = await naturezaOperacaoService
                .getNaturezaEquivalenteById(this.nota.naturezaOperacaoId)
                .resolveWithJSON<NaturezaOperacaoModel | null>()
                .catch(() => null);

            if (!data) {
                this.nota.naturezaOperacao = null;
                return;
            }

            this.nota.naturezaOperacaoId = data.id ?? this.nota.naturezaOperacaoId;
            this.nota.naturezaOperacao = data;
            await this.ensureNaturezaOperacaoNoCombo(this.nota.naturezaOperacaoId);
            this.nota.notaImportacao = data.codigo.toString().substring(0, 1) == "3";
            this.nota.notaExportacao = data.codigo.toString().substring(0, 1) == "7";
            this.nota.vendaInterestadual = data.codigo.toString().substring(0, 1) == "6";
            if (data.observacao) {
                this.nota.observacao = data.observacao;
            }

            if (this.nota.destinatario && data.observacoes && data.observacoes.length > 0) {
                const destinatario = this.nota.destinatario as ClienteModel | FornecedorModel;
                const ufDestinatario = destinatario.estadoId;
                const observacaoUF = data.observacoes.find(obs => obs.uf === ufDestinatario);

                if (observacaoUF) {
                    this.nota.observacao = observacaoUF.observacao;
                }
            }
            if (this.nota.tipoCadastro == TipoCadastro.Fornecedor) {
                await this.carregarFornecedores();
            }

            if (
                !this.consulta &&
                (this.nota.notaImportacao || this.nota.notaExportacao) &&
                this.fornecedorOptions.filter(item => item.value == this.nota.destinatarioId).length == 0
            ) {
                this.nota.destinatarioId = null;
            }

            if (
                data.usaConfiguracaoFiscal &&
                this.vendaModel != null &&
                !skipConfiguracaoFiscal &&
                !skipReloadProduto
            ) {
                const naturezaOperacaoId = this.nota.naturezaOperacaoId;

                if (this.notaTransporte) {
                    while (this.loadingNotaTransporte) await Delay(100);

                    await this.loadModelByTransporte(this.vendaModel, true);
                } else {
                    while (this.loadingNotaByVenda) await Delay(100);

                    await this.loadModelByVenda(this.vendaModel, true);
                }

                try {
                    this.skipConfiguracaoFiscalReload = true;
                    this.nota.naturezaOperacaoId = naturezaOperacaoId;
                    await this.onNaturezaChange();
                } finally {
                    this.skipConfiguracaoFiscalReload = false;
                }
                return;
            }
            if (this.nota.naturezaOperacao.importacao) {
                // Avisa uma única vez por natureza selecionada: as demais passagens por este método
                // (reload da configuração fiscal, load da consulta, reentrada do watcher) apenas
                // reaplicam a restrição, sem repetir a notificação.
                const avisaNaoMovimentaEstoque =
                    !this.consulta && this.naturezaImportacaoAvisadaId != this.nota.naturezaOperacaoId;

                this.nota.movimentarEstoque = false;
                this.naturezaImportacaoAvisadaId = this.nota.naturezaOperacaoId;

                if (avisaNaoMovimentaEstoque) {
                    this.$notifyWarn(this.$t("__.ts.cfopImportacaoNaoMovimentaEstoque") as string);
                }
            } else {
                this.naturezaImportacaoAvisadaId = null;
                this.nota.movimentarEstoque = this.nota.naturezaOperacao.movimentaEstoque;
            }
            this.nota.naturezaOperacao.simplesRemessa = data.simplesRemessa;

            const destinatarioEstadoId = (this.nota.destinatario as { estadoId?: number } | null)?.estadoId;
            const outroEstado =
                destinatarioEstadoId != null && this.usuarioLogado.franquia.estadoId !== destinatarioEstadoId;

            this.nota.itens.forEach(item =>
                loadImpostosByNaturezaOperacao(
                    item,
                    this.nota.naturezaOperacaoId!,
                    this.clienteId,
                    this.vendaModel.tipoVendaId,
                    outroEstado,
                    false,
                    this.franquiaFracionadora,
                ),
            );
        }
    }

    // @ts-ignore
    @Watch("nota.tipoOperacao")
    private onChangeTipoOperacao() {
        if (!this.consulta && !this.notaTransporte) {
            this.nota.naturezaOperacaoId = null;
            this.carregarCfops();

            if (this.nota.tipoOperacao == TipoOperacao.Entrada) {
                this.nota.modelo = 55;
                this.nota.tipoCadastro = TipoCadastro.Fornecedor;
            } else {
                this.nota.tipoCadastro = TipoCadastro.Cliente;
            }
        }
    }

    // @ts-ignore
    @Watch("nota.finalidadeEmissao")
    private async onChangeFinalidadeEmissao() {
        if (this.nota.finalidadeEmissao == 4) {
            if (this.nota.tipoOperacao == TipoOperacao.Entrada) {
                this.nota.tipoCadastro = TipoCadastro.Cliente;
            } else {
                this.nota.tipoCadastro = TipoCadastro.Fornecedor;
            }
        }
        if (this.nota.finalidadeEmissao == 2) {
            this.nota.indicadorPresenca = 0;
        }
        if (this.franquiaGrupoAtlas && !this.consulta) {
            await this.carregarCfops();
        }
    }

    // @ts-ignore
    @Watch("nota.destinatarioId")
    private async onChangeDestinatarioId() {
        if (this.nota.destinatarioId) {
            if (this.nota.tipoCadastro == TipoCadastro.Fornecedor) {
                this.nota.destinatario = await this.fornecedorService
                    .get(this.nota.destinatarioId)
                    .resolveWithJSON<FornecedorModel>();

                await this.fornecedorService.validarValidadeLicensaSanitaria(this.nota.destinatarioId).withLoading();
            } else {
                this.clienteId = this.nota.destinatarioId;
                const cliente = await this.clienteService.get(this.nota.destinatarioId).resolveWithJSON<ClienteModel>();

                this.nota.destinatario = cliente;

                if (cliente.indicadorOperacao != null) {
                    this.nota.indicadorOperacao = (this.nota.destinatario as ClienteModel).indicadorOperacao;
                }
            }

            if (!this.emissaoVenda && !this.loadingConsulta) {
                this.baixaEstoque = await this.perguntaBaixaEstoque(this.nota.destinatario);
            }
        }
    }

    // @ts-ignore
    @Watch("clienteId")
    private onChangeClienteId() {
        this.nota.destinatarioId = this.clienteId;
    }

    @Watch("nota.fracionarLotes")
    private onChangeFracionaLotes() {
        if (this.nota.fracionarLotes == true) {
            this.nota.movimentarEstoque = true;
        }
    }

    private async perguntaBaixaEstoque(destinatario: object) {
        if (this.consulta) return;

        if (destinatario["cnpj"] == this.usuarioLogado.franquia.cnpj) {
            const question =
                "O sistema verificou que é uma nota de baixa de estoque, deseja carregar automaticamente os percentuais de ICMS da compra para os itens?";
            try {
                return await this.$showQuestionWithHTML(
                    this.$t("__.ts.atencao"),
                    `<div id="swal2-content" class="swal2-content" style="display: block;">${question}</div>`,
                );
            } catch {}
        }
        return false;
    }

    private async showQtdItensAlteradosCompra(produtoAlteradosCompra) {
        if (produtoAlteradosCompra.length == 0) {
            await this.$showWarning(
                "Nenhum item alterado!",
                "Não foram alterados nenhum dos itens com os dados da compra. Isso se deve ao fato de que não foram encontrados registro de compra com os produtos destes itens. É necessário fazer o ajuste manualmente.",
            );
        } else {
            let itensAlterados = 0;
            this.nota.itens.forEach(i => {
                if (!produtoAlteradosCompra.indexOf(i.produtoId)) itensAlterados++;
            });
            await this.$showSuccess(
                "Itens alterados!",
                `Foram alterados ${itensAlterados} de ${this.nota.itens.length} itens.`,
            );
        }
    }

    // @ts-ignore
    @Watch("nota.modelo")
    private onChangeModelo(newValue: number) {
        if (this.consulta) return;

        if (this.nota.naturezaOperacaoId != null && newValue != null) {
            if (this.nota.naturezaOperacao?.codigo?.toString().substring(0, 1) != "5") {
                this.nota.naturezaOperacaoId = null;
            }
            this.carregarCfops();
        }

        if (this.nota.modelo == 65) {
            this.nota.tipoOperacao = TipoOperacao.Saida;
            this.nota.tipoCadastro = TipoCadastro.Cliente;
            this.nota.indicadorOperacao = 1;
        } else {
            this.nota.indicadorOperacao = 0;
        }

        if (this.nota.modelo == 55) this.nota.semDestinatario = false;
    }

    private async carregarCfops() {
        const data = await naturezaOperacaoService.comboCached(this.usuarioLogado.franquiaId);
        const finalidadeEmissao = this.nota.finalidadeEmissao;
        // Regra restrita ao Grupo Atlas: fora dele, todos os CFOPs da movimentação aparecem,
        // independente da finalidade de emissão selecionada no cabeçalho. "0" é o valor antes
        // de qualquer seleção (default do model) — nenhuma finalidade real usa esse valor.
        const filtraPorFinalidade = this.franquiaGrupoAtlas && !this.consulta && !!finalidadeEmissao;

        let options = data;
        if (this.nota.modelo == 65) {
            options = data.filter(
                item =>
                    item.tipoMovimentacao == TiposMovimentacao.Saida &&
                    item.dentroEstado &&
                    (!filtraPorFinalidade || item.finalidadesEmissao.includes(finalidadeEmissao)),
            );
        } else {
            options = data.filter(
                item =>
                    item.tipoMovimentacao ==
                        (this.nota.tipoOperacao == TipoOperacao.Entrada
                            ? TiposMovimentacao.Entrada
                            : TiposMovimentacao.Saida) &&
                    (!filtraPorFinalidade || item.finalidadesEmissao.includes(finalidadeEmissao)),
            );
        }
        if (
            this.notaTransporte &&
            this.clienteAtualTipoCliente !== null &&
            this.clienteAtualTipoCliente !== TipoCliente.Filial
        ) {
            options = options.filter(item => !item.text.startsWith("5557") && !item.text.startsWith("6557"));
        } else if (
            this.notaTransporte &&
            this.clienteAtualTipoCliente !== null &&
            this.clienteAtualTipoCliente === TipoCliente.Filial
        ) {
            options = options.filter(item => item.text.startsWith("5557") || item.text.startsWith("6557"));
        }

        // A natureza selecionada entra na lista antes de atribuir a combo. Se a combo receber uma
        // lista sem ela, o próprio componente limpa o v-model e o cabeçalho perde o CFOP — a
        // reatribuição seguinte dispara o watcher da natureza e reentra no reload em loop.
        const naturezaSelecionada =
            this.nota.naturezaOperacaoId && !options.some(p => p.value == this.nota.naturezaOperacaoId)
                ? data.find(p => p.value == this.nota.naturezaOperacaoId)
                : null;

        if (naturezaSelecionada) {
            // Fora da lista por movimentação/transporte a natureza volta para a combo (evita o loop
            // acima). Só quando o CFOP selecionado não permite a finalidade de emissão escolhida ela
            // é limpa com aviso — emitir com essa combinação não é permitido.
            if (filtraPorFinalidade && !naturezaSelecionada.finalidadesEmissao.includes(finalidadeEmissao)) {
                this.nota.naturezaOperacaoId = null;
                this.$notifyWarn(this.$t("__.ts.cfopNaoPermiteFinalidadeEmissao") as string);
            } else {
                options = [...options, naturezaSelecionada];
            }
        }

        this.cfopOptions = options.sort((a, b) => (a.text > b.text ? 1 : -1));
    }

    // Garante que a natureza selecionada no cabeçalho exista na combo, mesmo que não bata com o
    // filtro de movimentação da nota (ex.: natureza de Entrada do produto numa nota de Saída).
    // Assim o cabeçalho exibe o mesmo CFOP resolvido a partir do produto, sem divergir do item.
    private async ensureNaturezaOperacaoNoCombo(naturezaOperacaoId: number | null | undefined) {
        if (!naturezaOperacaoId) {
            return;
        }
        if (this.cfopOptions.some(p => p.value == naturezaOperacaoId)) {
            return;
        }
        const data = await naturezaOperacaoService.comboCached(this.usuarioLogado.franquiaId ?? 0);
        const natureza = data.find(p => p.value == naturezaOperacaoId);
        if (natureza) {
            // Nova referência para a combo reprocessar os dados e reencontrar o valor selecionado:
            // o push isolado não notifica o watcher de `data` do componente.
            this.cfopOptions = [...this.cfopOptions, natureza].sort((a, b) => (a.text > b.text ? 1 : -1));
        }
    }

    private async carregarFornecedores() {
        const data = await this.fornecedorService
            .combo()
            .then(r => r.json() as Promise<PaginationModel<FornecedorModel>>);
        data.list = data.list.filter(item => (this.nota.notaImportacao || this.nota.notaExportacao) == item.exterior);
        this.fornecedorOptions = data.list.map(getFornecedorCombo);
    }

    private async consultaEmissao() {
        try {
            let sucesso = false;
            if (this.nota.origemEmissao == OrigemEmissao.EmissaoEmLote) {
                sucesso = await this.service.consultarBySearchKey(this.nota.searchKey).resolveWithoutJSON();
            } else {
                sucesso = await this.service.consultar(this.nota.recibo, this.nota.modelo).resolveWithoutJSON();
            }

            if (sucesso) {
                this.$showSuccess("Confirmada!", "Consulta de retorno com sucesso, Nota Fiscal foi aprovada");
                this.load();
            }
        } catch {}
    }

    private async openNewNaturezaOperacao() {
        this.naturezaOperacaoComponent = this.$refs.naturezaOperacaoComponent as NaturezaOperacaoEditComponent;
        this.naturezaOperacaoComponent.modelId = null;
        this.naturezaOperacaoComponent.load();
    }

    private async onAddNaturezaOperacao(closeModal) {
        if (await this.naturezaOperacaoComponent.save()) {
            closeModal();
        }
    }

    private async openComboEditNaturezaOperacao(id: number) {
        this.naturezaOperacaoComponent = this.$refs.naturezaOperacaoComponent as NaturezaOperacaoEditComponent;
        this.naturezaOperacaoComponent.modelId = id;
        this.naturezaOperacaoComponent.load();
    }

    private async onNaturezaOperacaoSaveOk() {
        await this.carregarCfops();
    }

    private async openNewCliente() {
        this.clienteComponent = this.$refs.clienteComponent as ClienteEditComponent;
        this.clienteComponent.modelId = null;
        this.clienteComponent.load();
    }

    private async openComboEditCliente(id: number) {
        this.clienteComponent = this.$refs.clienteComponent as ClienteEditComponent;
        this.clienteComponent.modelId = id;
        this.clienteComponent.load();
    }

    private async onAddCliente(closeModal) {
        if (await this.clienteComponent.save()) {
            closeModal();
        }
    }

    private async onClienteSaveOk() {}

    private async openNewFornecedor() {
        this.fornecedorComponent = this.$refs.fornecedorComponent as FornecedorEditComponent;
        this.fornecedorComponent.modelId = null;
        this.fornecedorComponent.load();
    }

    private async onAddFornecedor(closeModal) {
        if (await this.fornecedorComponent.save()) {
            closeModal();
        }
    }

    private async openComboEditFornecedor(id: number) {
        this.fornecedorComponent = this.$refs.fornecedorComponent as FornecedorEditComponent;
        this.fornecedorComponent.modelId = id;
        this.fornecedorComponent.load();
    }

    private async onFornecedorSaveOk() {
        await this.carregarFornecedores();
    }

    public async resumo() {
        if (await this.validateNFE()) {
            this.shortcutComponent = this.$refs.shortcutComponent as ShortcutComponent;
            this.resumoComponent = this.$refs.resumoComponent as ResumoComponent;

            const result = await this.resumoComponent.load(this.nota).withLoading();
            if (result) {
                this.shortcutComponent.show();
                this.shortcutComponent.title = "Resumo da emissão de Nota Fiscal";
            }
        }
    }

    public async onChangeNaturezaOperacao(naturezaId: Number) {
        if (
            this.nota.indicadorPresenca == 1 &&
            (this.cfopOptions.find(p => p.value == naturezaId)["text"][0] == "2" ||
                this.cfopOptions.find(p => p.value == naturezaId)["text"][0] == "6")
        ) {
            this.$showWarning("NF-e", "Selecionado natureza de operação interestadual e operação presencial");
        }
    }

    public async onChangeIndicadorDePresenca(indicadorDePresencaId: Number) {
        const naturezaOperacao = this.cfopOptions.find(p => p.value == this.nota.naturezaOperacaoId);
        if (
            indicadorDePresencaId == 1 &&
            naturezaOperacao != null &&
            (naturezaOperacao["text"][0] == "2" || naturezaOperacao["text"][0] == "6")
        ) {
            this.$showWarning("NF-e", "Selecionado operação presencial e natureza de operação interestadual");
        }
    }

    private openPDF() {
        this.service.openPdf(this.nota.pdfLink);
    }

    private openPDFSimplificado() {
        this.service.openPdf(this.nota.pdfSimplificadoLink);
    }

    private openXML() {
        this.service.downloadXML(this.nota.xmlLink);
    }

    private openCartaCorrecao() {
        this.service.showCartaCorrecao(this.modelId, this.nota.tipoOperacao);
    }

    private async onGerarCartaCorrecao() {
        const response = await this.service.cartaCorrecaoSelecao();

        if (response.isConfirmed) {
            try {
                const sucesso = await this.service
                    .cartaCorrecao(this.nota.chave, this.modelId, response.value, this.nota.tipoOperacao)
                    .withLoading()
                    .resolveWithoutJSON();

                if (sucesso) {
                    this.load();
                }
            } catch {}
        }
    }

    private async onCancelar() {
        const response = await this.service.cancelamentoSelecao();

        if (response.isConfirmed) {
            try {
                const sucesso = await this.service
                    .cancelamento(this.nota.chave, response.value)
                    .withLoading()
                    .resolveWithoutJSON();

                if (sucesso) {
                    this.load();
                }
            } catch {}
        }
    }

    private async mounted() {
        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO);
        Bus.$emit("hide-nav-menu", true);

        this.itemListComponent = this.$refs.itemListComponent as ItemNFEComponent;
        this.exportacaoComponent = this.$refs.exportacaoComponent as ExportacaoComponent;

        this.naturezaOperacaoComponent = this.$refs.naturezaOperacaoComponent as NaturezaOperacaoEditComponent;
        this.clienteComponent = this.$refs.clienteComponent as ClienteEditComponent;
        this.fornecedorComponent = this.$refs.fornecedorComponent as FornecedorEditComponent;
        this.shortcutComponent = this.$refs.shortcutComponent as ShortcutComponent;

        this.SET_NOTA(new NotaFiscalEletronicaEmissaoModel());
        this.SET_NOTA_CONSULTA(false);
        this.SET_NOTA_EMISSAO_VENDA(false);

        if (!this.GET_CONFIG_FRANQUIA(Configuracoes.EmiteNotaFiscalConsumidor)?.verdadeiro) {
            this.modeloOptions = this.modeloOptions.filter(o => o.value !== 65);
        }

        if (this.$route.params.id) {
            this.modelId = this.$route.params.id;
        }

        this.franquiaGrupoAtlas = this.usuarioLogado.franquia?.franquiaGrupoAtlas;

        const response = await Promise.all([
            estoqueService.comboCached(this.usuarioLogado.franquiaId),
            this.carregarFornecedores(),
            this.carregarCfops(),
            this.load(),
        ])
            .withLoading()
            .catch(() => {});

        this.estoqueOptions = response[0];
    }

    // Resolve o CFOP para a variante de mesmo código com "consumidor final" marcado.
    // Retorna o próprio id caso não exista variante (fallback do backend).
    private async resolverCFOPConsumidorFinal(naturezaOperacaoId: number): Promise<number> {
        return await naturezaOperacaoService
            .getNaturezaConsumidorFinalById(naturezaOperacaoId ?? 0)
            .withLoading()
            .resolveWithJSON<number>();
    }

    // Alinha o CFOP do item à variante "consumidor final" (mesmo código, tributação diferente) e
    // recarrega os impostos a partir da natureza resolvida — o cabeçalho sozinho não altera os itens.
    private async aplicarCFOPConsumidorFinalNoItem(
        item: NotaFiscalEletronicaEmissaoItemModel,
        aplica: boolean,
        outroEstado: boolean,
    ) {
        if (!aplica || item.naturezaOperacaoId == null) {
            return item;
        }
        item.naturezaOperacaoId = await this.resolverCFOPConsumidorFinal(item.naturezaOperacaoId);
        return await loadImpostosByNaturezaOperacao(
            item,
            item.naturezaOperacaoId,
            this.clienteId,
            this.vendaModel.tipoVendaId,
            outroEstado,
            true,
            this.franquiaFracionadora,
        );
    }
}
